import React, {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useState,
} from "react";
import {
    addToFavourites,
    fetchFavouritesList,
    removeFromFavourites,
} from "../../api/apiService";
import {IMotorcycle} from "../../interfaces/Interfaces";

interface FavouritesContextType {
    favouritesIds: number[];
    favouritesList: IMotorcycle[];
    toggleFavourite: (id: number) => void;
    favouritesLoading: boolean;
}

const FavouritesContext = createContext<FavouritesContextType | undefined>(undefined);

interface FavouritesProviderProps {
    children: ReactNode;
}

export const FavouritesProvider: React.FC<FavouritesProviderProps> = ({children}) => {
    const [favouritesIds, setFavouritesIds] = useState<number[]>([]);
    const [favouritesList, setFavouritesList] = useState<IMotorcycle[]>([]);
    const [favouritesLoading, setFavouritesLoading] = useState<boolean>(true);  // Track loading state

    const getAuthenticatedUser = () => {
        const storedUser = localStorage.getItem("fbUser");
        if (!storedUser) return null;
        const fbUser = JSON.parse(storedUser);
        return fbUser.id && fbUser.session ? fbUser : null;
    };

    useEffect(() => {
        const loadFavourites = async () => {
            const fbUser = getAuthenticatedUser();
            if (!fbUser) return;

            setFavouritesLoading(true);

            try {
                const fetchedFavourites = await fetchFavouritesList(fbUser.id, fbUser.session);
                const favIds = fetchedFavourites.map((moto) => moto.id);
                setFavouritesIds(favIds);
                setFavouritesList(fetchedFavourites);
            } catch (error) {
                console.error("Error fetching favourites:", error);
            } finally {
                setFavouritesLoading(false);
            }
        };

        loadFavourites();
    }, []);

    const toggleFavourite = async (motorcycleId: number) => {
        const fbUser = getAuthenticatedUser();
        if (!fbUser) {
            console.error("User not authenticated, cannot modify favourites.");
            return;
        }

        let updatedFavouritesIds = [...favouritesIds];
        let updatedFavouritesList = [...favouritesList];

        if (favouritesIds.includes(motorcycleId)) {
            try {
                await removeFromFavourites(motorcycleId, fbUser.id, fbUser.session);
                updatedFavouritesIds = updatedFavouritesIds.filter((id) => id !== motorcycleId);
                updatedFavouritesList = updatedFavouritesList.filter(
                    (motorcycle) => motorcycle.id !== motorcycleId,
                );
            } catch (error) {
                console.error("Error removing from favourites:", error);
                return;
            }
        }
        else {
            try {
                await addToFavourites(motorcycleId, fbUser.id, fbUser.session);
                updatedFavouritesIds = [...favouritesIds, motorcycleId];
                const newMotorcycle = await fetchFavouritesList(fbUser.id, fbUser.session)
                    .then((fetchedFavourites) =>
                        fetchedFavourites.find((moto) => moto.id === motorcycleId),
                    );
                if (newMotorcycle) {
                    updatedFavouritesList = [...favouritesList, newMotorcycle];
                }
            } catch (error) {
                console.error("Error adding to favourites:", error);
                return;
            }
        }

        setFavouritesIds(updatedFavouritesIds);
        setFavouritesList(updatedFavouritesList);
    };

    return (
        <FavouritesContext.Provider value={{favouritesIds, favouritesList, toggleFavourite, favouritesLoading}}>
            {children}
        </FavouritesContext.Provider>
    );
};

export const useFavourites = (): FavouritesContextType => {
    const context = useContext(FavouritesContext);
    if (!context) {
        throw new Error("useFavourites must be used within a FavouritesProvider");
    }
    return context;
};
